import { isAxiosError } from 'axios';
import { makeAutoObservable } from 'mobx';

import {
  CatalogRequests,
  type FilterChoiceAPI,
  type ProductPropertiesKeyAPI,
  type ProductsPagedResponse,
  type SortingFieldName,
  type SortingOrder,
} from '~/api/Catalog';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import { catalogStore, mainStore } from '~/stores';
import type {
  ProductFilters,
  ProductPropertyFilter,
  ProductRangeFilter,
} from '~/stores/CatalogStore';
import { Category } from '~/stores/CategoriesStore/Category';
import { Product } from '~/stores/CategoriesStore/Product';
import { Pagination } from '~/stores/shared/Pagination';
import { PreloadCache } from '~/utils/preloadCache';

const PRELOAD_CACHE_MAX = 500;
const PRELOAD_CACHE_TTL_MS = 60_000;

type Sorting = [SortingFieldName, SortingOrder];

interface ProductParams {
  sorting?: Nullable<Sorting>;
  filters?: Nullable<ProductFilters>;
}

export const ignoreParams = [
  'sorting',
  'sortingDir',
  'priceFrom',
  'priceTo',
  'modal',
  'editAddress',
  'page',
  'showProducts',
  // Marketing / tracking params — must not be treated as product filters
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
  'fbclid',
  'gclid',
  'gclsrc',
  'dclid',
  'msclkid',
  'twclid',
  'ttclid',
  'li_fat_id',
  'mc_cid',
  'mc_eid',
  'yclid',
  '_ga',
  'ref',
];

export class CatalogCategory {
  private static instance: CatalogCategory | null = null;
  private category: Nullable<Category> = null;
  private readonly id: string | number;
  private preloadNotFound = false;

  private static preloadedData = new PreloadCache<ProductsPagedResponse>({
    max: PRELOAD_CACHE_MAX,
    ttlMs: PRELOAD_CACHE_TTL_MS,
  });
  private static preloadedNotFound = new PreloadCache<true>({
    max: PRELOAD_CACHE_MAX,
    ttlMs: PRELOAD_CACHE_TTL_MS,
  });

  constructor(categoryId: string | number) {
    this.id = categoryId;
    this.init();
    makeAutoObservable(this);
  }

  public static getInstance(categoryId: string | number): CatalogCategory {
    if (
      !CatalogCategory.instance ||
      categoryId !== CatalogCategory.instance.id
    ) {
      CatalogCategory.instance = new CatalogCategory(categoryId);
    }
    return CatalogCategory.instance!;
  }

  public static clearAllInstances() {
    CatalogCategory.instance = null;
  }

  public static request(
    categoryId: string | number,
    page: number,
    warehouse: string,
    serverLang?: string,
    { filters, sorting }: ProductParams = {},
  ) {
    const params = catalogStore.getProductFilters(
      filters || catalogStore.currentFilters,
      sorting || catalogStore.currentSorting,
      page,
    );

    return CatalogRequests.getProductsPaged(
      String(categoryId),
      {
        warehouseCode: warehouse,
        isSafeRequest: mainStore.isSafeRequest || undefined,
        ...params,
      },
      serverLang,
    );
  }

  public static async preload(
    categoryId: string | number,
    warehouse: string,
    serverLang?: string,
    params?: ProductParams,
  ) {
    try {
      const data = await CatalogCategory.request(
        categoryId,
        1,
        warehouse,
        serverLang,
        params,
      );

      CatalogCategory.preloadedData.set(String(categoryId), data);
      CatalogCategory.preloadedNotFound.delete(String(categoryId));

      return data;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        CatalogCategory.preloadedNotFound.set(String(categoryId), true);
      }
      throw error;
    }
  }

  public static removePreloadedData(categoryId: string | number) {
    CatalogCategory.preloadedData.delete(String(categoryId));
    CatalogCategory.preloadedNotFound.delete(String(categoryId));
  }

  public static parseSearchParams(
    search: URLSearchParams | string,
  ): ProductParams {
    try {
      const searchParams = toSearchParams(search);
      const sorting = parseSorting(searchParams);
      const properties = parseProperties(searchParams);
      const price = parsePrice(searchParams);

      return {
        sorting,
        filters: { properties, price },
      };
    } catch {
      return {};
    }
  }

  public get data(): Nullable<Omit<Category, 'products' | 'productsRequest'>> {
    return this.category;
  }

  public get products() {
    return this.paginationRequest.result;
  }

  public get types(): FilterChoiceAPI[] {
    if (!this.category) {
      return [];
    }

    return this.category.subcategory.map(
      ({ id, name, subcategory, products }) => ({
        label: name,
        value: id.toString(),
        count: products.length,
        isFinal: !subcategory.length,
      }),
    );
  }

  public get isNotFound() {
    if (this.preloadNotFound) {
      return true;
    }
    return isAxiosError(this.paginationRequest.lastError)
      ? this.paginationRequest.lastError.response?.status === 404
      : false;
  }

  public get isReady() {
    return Boolean(this.category);
  }

  public readonly paginationRequest = new Pagination(
    async (page, warehouse: string) => {
      const isSSR = typeof window === 'undefined';
      if (!isSSR) {
        delete window.__INITIAL_STATE__?.catalogCategory;
      }
      const { data, pagination } = await CatalogCategory.request(
        this.id,
        page,
        warehouse,
      );

      this.category = new Category(data);

      analyticsService.track(AnalyticsEvent.VIEW_ITEM_LIST, {
        item_list_name: this.category.name,
        item_list_id: this.category.id,
      });

      return {
        pagination,
        data: data.products?.map((data) => new Product(data)) || [],
      };
    },
    { debounce: 700 },
  );

  private init() {
    const isSSR = typeof window === 'undefined';

    if (isSSR && CatalogCategory.preloadedNotFound.has(String(this.id))) {
      this.preloadNotFound = true;
      return;
    }

    const preloadedData =
      (!isSSR ? window.__INITIAL_STATE__?.catalogCategory : null) ||
      CatalogCategory.preloadedData.get(String(this.id));

    if (!preloadedData || preloadedData.data.category.slug !== this.id) {
      return;
    }

    const { data, pagination } = preloadedData;

    this.category = new Category(data);
    this.paginationRequest.setState({
      pagination,
      data: data.products?.map((data) => new Product(data)) || [],
    });
  }
}

const parseProperties = (searchParams: URLSearchParams) => {
  return Array.from(searchParams.keys()).reduce((acc, key) => {
    if (ignoreParams.includes(key)) {
      return acc;
    }

    return {
      ...acc,
      [key as ProductPropertiesKeyAPI]: searchParams
        .getAll(key)
        .flatMap((param) => param.split(',')),
    };
  }, {} as ProductPropertyFilter);
};

const parsePrice = (searchParams: URLSearchParams) => {
  const price: ProductRangeFilter = {};
  const priceFrom = +String(searchParams.get('priceFrom'));
  const priceTo = +String(searchParams.get('priceTo'));

  if (priceFrom) {
    price.from = priceFrom;
  }

  if (priceTo) {
    price.to = priceTo;
  }

  return price;
};

const parseSorting = (searchParams: URLSearchParams) => {
  const sorting = searchParams.get('sorting');
  const sortingDir = searchParams.get('sortingDir');

  if (sorting && sortingDir) {
    return [sorting, sortingDir] as [SortingFieldName, SortingOrder];
  }
};

const toSearchParams = (url: string | URLSearchParams): URLSearchParams => {
  if (url instanceof URLSearchParams) {
    return url;
  }

  const search = url.split('?')[1];

  return new URLSearchParams(search);
};
